import { useMemo } from 'react';
import { useQuery } from 'react-query';
import qs from 'qs';
import urljoin from 'url-join';
import { subDays, endOfDay, startOfDay } from 'date-fns';

/**
 * Single-roundtrip dashboard stats. Calls the BFF route that proxies
 * `/analytics/dashboard-stats` on the Order Management API.
 *
 * Replaces the older fan-out (4× `/orders?pageSize=1`) implementation.
 * Until the BFF route deploys to a given environment, react-query
 * returns the 404 as an error and the dashboard renders its loading /
 * placeholder states — no crash.
 */

const DASHBOARD_STATS_ENDPOINT = '/analytics/dashboard-stats';

export type DashboardStatusBucket = { status: string; count: number };
export type DashboardDailyBucket = {
  date: string;
  count: number;
  revenue: number;
};
export type DashboardCityBucket = {
  city: string;
  count: number;
  revenue: number;
};

export interface DashboardStatsResponse {
  from: string;
  to: string;
  totalOrders: number;
  pendingOrders: number;
  failedOrders: number;
  deliveredOrders: number;
  ordersToday: number;
  totalRevenue: number;
  statusBreakdown: DashboardStatusBucket[];
  dailyBreakdown: DashboardDailyBucket[];
  cityBreakdown: DashboardCityBucket[];
}

export interface DashboardSummaryParams {
  city?: string;
  from?: Date;
  to?: Date;
  entityIds?: string[];
}

type Counts = { value: number; isLoading: boolean; error: boolean };

export type DashboardSummary = {
  ordersToday: Counts;
  pending: Counts;
  failed7d: Counts;
  lowStock: Counts;
  totalProducts: Counts;
  revenue: Counts;
  raw: DashboardStatsResponse | undefined;
  isLoading: boolean;
  error: boolean;
};

/**
 * Build a deterministic URL with the query params on it so react-query
 * caches per-filter without us threading a queryKey by hand.
 */
const buildUrl = (params: DashboardSummaryParams) => {
  const query = qs.stringify(
    {
      ...(params.city && params.city !== 'all' ? { city: params.city } : {}),
      ...(params.from
        ? { from: startOfDay(params.from).toISOString() }
        : {}),
      ...(params.to ? { to: endOfDay(params.to).toISOString() } : {}),
    },
    { addQueryPrefix: true, skipNulls: true }
  );
  return urljoin(DASHBOARD_STATS_ENDPOINT, query);
};

export const useDashboardStats = (params: DashboardSummaryParams = {}) => {
  const url = useMemo(() => buildUrl(params), [params.city, params.from, params.to]);
  // Include a sorted, stable entity-ID string as a second query key segment.
  // This gives react-query a separate cache slot per entity selection so
  // switching between selections returns the right cached data without
  // wiping the cache. The actual entity filtering happens via the
  // SelectedEntities header that the axios interceptor attaches.
  const entityKey = useMemo(
    () => [...(params.entityIds ?? [])].sort().join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(params.entityIds)]
  );
  return useQuery<UseQueryResponse<DashboardStatsResponse>>([url, entityKey], {
    // Stale-tolerant — the dashboard is a snapshot, refreshing on
    // every mount creates jitter without much value.
    staleTime: 30_000,
  });
};

/**
 * Adapter that exposes the dashboard stats in the same `Counts` shape
 * the page already consumes. Keeps the page render unchanged.
 *
 * For the "failed7d" card we pull the failed count from the 7-day
 * window (computed locally — single network call still covers it
 * because the upstream window can be parameterised; we ask for the
 * caller's window and the per-status breakdown lets us slice it).
 */
export const useDashboardSummary = (
  params: DashboardSummaryParams = {}
): DashboardSummary => {
  // Default window: last 7 days. The Failed (7d) card is what we
  // anchor on; other cards reference the same window for consistency.
  const to = useMemo(() => params.to ?? new Date(), [params.to]);
  const from = useMemo(
    () => params.from ?? subDays(to, 7),
    [params.from, to]
  );

  const { data, isLoading, isError } = useDashboardStats({
    city: params.city,
    from,
    to,
    entityIds: params.entityIds,
  });

  const raw = data?.response;
  const wrap = (v: number | undefined): Counts => ({
    value: v ?? 0,
    isLoading,
    error: isError,
  });

  return {
    ordersToday: wrap(raw?.ordersToday),
    pending: wrap(raw?.pendingOrders),
    failed7d: wrap(raw?.failedOrders),
    // Low stock + totalProducts aren't part of the order stats payload —
    // those will need a separate /products aggregate when it exists.
    // For now they surface as 0 with no error so the cards render.
    lowStock: { value: 0, isLoading, error: false },
    totalProducts: { value: 0, isLoading, error: false },
    revenue: wrap(raw ? Math.round(raw.totalRevenue) : undefined),
    raw,
    isLoading,
    error: isError,
  };
};

export const formatStat = (c: Counts): string => {
  if (c.error) return '—';
  if (c.isLoading) return '…';
  return c.value.toLocaleString();
};
